import { useMemo, useState } from "react";
import SyringeViz from "@/components/SyringeViz";

const SYRINGES = [
  { id: "u30", label: "0.3 mL (30 units)", units: 30, ml: 0.3 },
  { id: "u50", label: "0.5 mL (50 units)", units: 50, ml: 0.5 },
  { id: "u100", label: "1.0 mL (100 units)", units: 100, ml: 1.0 },
];

const VIAL_PRESETS = [10, 16, 30];
const BAC_PRESETS = [1, 2, 3];
const DOSE_PRESETS = [1, 2, 3, 4];

const inp =
  "bg-[#121212] border border-[#262626] text-white rounded-md px-4 py-3 focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-all font-mono-data text-sm";

export default function HGHCalculator() {
  const [syringeId, setSyringeId] = useState("u30");
  const [vialIU, setVialIU] = useState(10);
  const [iuPerMg, setIuPerMg] = useState(3);
  const [bacMl, setBacMl] = useState(1);
  const [doseIU, setDoseIU] = useState(2);

  const syringe = SYRINGES.find((s) => s.id === syringeId) || SYRINGES[0];

  const result = useMemo(() => {
    const v = parseFloat(vialIU);
    const b = parseFloat(bacMl);
    const d = parseFloat(doseIU);
    if (!v || !b || !d) return null;
    const concentrationIuPerMl = v / b;
    const mlNeeded = d / concentrationIuPerMl;
    const unitsNeeded = mlNeeded * (syringe.units / syringe.ml);
    const dosesPerVial = v / d;
    return {
      concentrationIuPerMl,
      mlNeeded,
      unitsNeeded,
      dosesPerVial,
      overflow: unitsNeeded > syringe.units,
    };
  }, [vialIU, bacMl, doseIU, syringe.units, syringe.ml]);

  const vialMgEquivalent = useMemo(() => {
    const v = parseFloat(vialIU);
    const rate = parseFloat(iuPerMg);
    if (!v || !rate) return null;
    return v / rate;
  }, [vialIU, iuPerMg]);

  return (
    <div data-testid="hgh-calculator">
      {/* Intro */}
      <div className="mb-8">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#00E5FF] mb-2">★ HGH-Specific</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-black mb-2">HGH Dosage Calculator</h2>
        <p className="text-[#A3A3A3] max-w-xl text-sm">Instantly convert IU to syringe units for Somatropin / HGH vials. Defaults assume the WHO standard 1 mg ≈ 3 IU — edit if your label says otherwise.</p>
      </div>

      {/* Syringe viz */}
      <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-6 sm:p-10 mb-8">
        <SyringeViz units={result && !result.overflow ? result.unitsNeeded : 0} maxUnits={syringe.units} color="#00E5FF" />

        {result && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#262626] border border-[#262626] rounded-lg overflow-hidden">
            <HghStat k="Pull to" v={`${result.unitsNeeded.toFixed(1)} u`} highlight />
            <HghStat k="Volume" v={`${result.mlNeeded.toFixed(3)} mL`} />
            <HghStat k="Concentration" v={`${result.concentrationIuPerMl.toFixed(1)} IU/mL`} />
            <HghStat k="Doses/vial" v={result.dosesPerVial.toFixed(1)} />
          </div>
        )}

        {result?.overflow && (
          <div data-testid="hgh-overflow" className="mt-4 bg-[#FF3366]/10 border border-[#FF3366]/40 rounded p-4 text-sm text-[#FF3366]">
            ⚠ This dose exceeds the {syringe.units}-unit syringe capacity. Use more BAC water, a larger syringe, or split into multiple draws.
          </div>
        )}
      </div>

      {/* Form */}
      <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-6 sm:p-8 space-y-6">
        {/* Syringe selector */}
        <HghField label="Which syringe are you using?">
          <div className="grid grid-cols-3 gap-2">
            {SYRINGES.map((s) => (
              <button
                key={s.id}
                type="button"
                data-testid={`hgh-syringe-${s.id}`}
                onClick={() => setSyringeId(s.id)}
                aria-pressed={syringeId === s.id}
                className={`py-3 rounded-md font-mono-data text-xs font-bold uppercase tracking-wider transition-colors ${
                  syringeId === s.id
                    ? "bg-[#00E5FF] text-black border border-[#00E5FF]"
                    : "bg-[#121212] text-[#A3A3A3] border border-[#262626] hover:text-white"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </HghField>

        {/* Vial IU */}
        <HghField label="Your HGH vial — total IU" hint={vialMgEquivalent != null ? `≈ ${vialMgEquivalent.toFixed(2)} mg of HGH at ${iuPerMg} IU/mg` : undefined}>
          <div className="flex flex-wrap gap-2">
            <PresetRow
              testidPrefix="hgh-vial-preset"
              presets={VIAL_PRESETS}
              suffix="IU"
              value={parseFloat(vialIU)}
              onPick={(v) => setVialIU(v)}
            />
            <input
              data-testid="hgh-vial-iu"
              type="text"
              inputMode="decimal"
              pattern="[0-9]*\.?[0-9]*"
              value={vialIU}
              onChange={(e) => setVialIU(e.target.value)}
              className={`${inp} w-28`}
              aria-label="Vial IU custom"
            />
            <span className="self-center text-xs font-mono-data text-[#A3A3A3]">IU</span>
          </div>
        </HghField>

        {/* IU/mg conversion */}
        <HghField
          label="Conversion rate (mg ≈ IU)"
          hint="WHO standard is 1 mg ≈ 3 IU. Override only if your product label specifies a different potency."
        >
          <div className="flex items-center gap-3 text-sm font-mono-data text-[#A3A3A3]">
            <span>1 mg ≈</span>
            <input
              data-testid="hgh-iu-per-mg"
              type="text"
              inputMode="decimal"
              pattern="[0-9]*\.?[0-9]*"
              value={iuPerMg}
              onChange={(e) => setIuPerMg(e.target.value)}
              className={`${inp} w-24 text-center`}
            />
            <span>IU</span>
          </div>
        </HghField>

        {/* BAC water */}
        <HghField label="BAC water — how much are you adding?" hint="1 mL or 2 mL is most common. 1 mL gives the simplest math.">
          <div className="flex flex-wrap gap-2">
            <PresetRow
              testidPrefix="hgh-bac-preset"
              presets={BAC_PRESETS}
              suffix="mL"
              value={parseFloat(bacMl)}
              onPick={(v) => setBacMl(v)}
            />
            <input
              data-testid="hgh-bac"
              type="text"
              inputMode="decimal"
              pattern="[0-9]*\.?[0-9]*"
              value={bacMl}
              onChange={(e) => setBacMl(e.target.value)}
              className={`${inp} w-28`}
              aria-label="BAC mL custom"
            />
            <span className="self-center text-xs font-mono-data text-[#A3A3A3]">mL</span>
          </div>
        </HghField>

        {/* Dose IU */}
        <HghField label="Dose per injection — how many IU?" hint="Common starting doses range from 1–2 IU/day. Females typically run lower than males.">
          <div className="flex flex-wrap gap-2">
            <PresetRow
              testidPrefix="hgh-dose-preset"
              presets={DOSE_PRESETS}
              suffix="IU"
              value={parseFloat(doseIU)}
              onPick={(v) => setDoseIU(v)}
            />
            <input
              data-testid="hgh-dose"
              type="text"
              inputMode="decimal"
              pattern="[0-9]*\.?[0-9]*"
              value={doseIU}
              onChange={(e) => setDoseIU(e.target.value)}
              className={`${inp} w-28`}
              aria-label="Dose IU custom"
            />
            <span className="self-center text-xs font-mono-data text-[#A3A3A3]">IU</span>
          </div>
        </HghField>

        {/* Result summary */}
        {result && !result.overflow && (
          <div data-testid="hgh-summary" className="bg-[#121212] border border-[#00E5FF]/30 rounded-md p-4 text-sm font-mono-data text-[#A3A3A3]">
            <span className="text-[#00E5FF]">›</span> To get your <span className="text-white font-bold">{parseFloat(doseIU)} IU</span> dose using the <span className="text-white font-bold">{syringe.label.toLowerCase()}</span>, pull to <span className="text-[#00E5FF] font-bold">{result.unitsNeeded.toFixed(1)} units</span>. This vial contains <span className="text-white font-bold">{result.dosesPerVial.toFixed(1)} doses</span>.
          </div>
        )}
      </div>
    </div>
  );
}

function HghField({ label, hint, children }) {
  return (
    <div>
      <label className="text-sm font-semibold text-[#A3A3A3] mb-2 block">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-[#A3A3A3] mt-2 font-mono-data">{hint}</p>}
    </div>
  );
}

function HghStat({ k, v, highlight }) {
  return (
    <div className={`p-4 ${highlight ? "bg-[#00E5FF]" : "bg-[#0A0A0A]"}`}>
      <div className={`text-[10px] tracking-widest uppercase ${highlight ? "text-black/70" : "text-[#A3A3A3]"}`}>{k}</div>
      <div className={`font-mono-data font-bold text-xl mt-1 ${highlight ? "text-black" : "text-white"}`}>{v}</div>
    </div>
  );
}

function PresetRow({ testidPrefix, presets, suffix, value, onPick }) {
  return (
    <>
      {presets.map((p) => (
        <button
          key={p}
          type="button"
          data-testid={`${testidPrefix}-${p}`}
          onClick={() => onPick(p)}
          aria-pressed={value === p}
          className={`px-4 py-2 rounded-md font-mono-data text-xs font-bold uppercase tracking-wider transition-colors ${
            value === p
              ? "bg-[#00E5FF] text-black border border-[#00E5FF]"
              : "bg-[#121212] text-[#A3A3A3] border border-[#262626] hover:text-white"
          }`}
        >
          {p} {suffix}
        </button>
      ))}
    </>
  );
}
